import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  FlipHorizontal2Icon,
  MaximizeIcon,
  MinimizeIcon,
  RotateCcwIcon,
  RotateCwIcon,
  ScanIcon,
  XIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { cn } from 'cn'

export interface ViewerItem {
  src: string
  title: string
  /** File name offered by the Download button. */
  fileName?: string
}

interface ViewerApi {
  open: (items: ViewerItem[], index?: number) => void
}

const ViewerContext = createContext<ViewerApi | null>(null)

/** Opens the full-screen image viewer from anywhere. Without a provider (e.g. in a test) it does nothing. */
export const useImageViewer = (): ViewerApi => useContext(ViewerContext) ?? { open: () => undefined }

const MIN = 0.25
const MAX = 6

/** Hosts one image viewer for the whole app. */
export function ImageViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ items: ViewerItem[]; index: number } | null>(null)
  const api = useMemo<ViewerApi>(() => ({ open: (items, index = 0) => items.length && setState({ items, index }) }), [])
  return (
    <ViewerContext.Provider value={api}>
      {children}
      {state && <Viewer key={state.items[0]?.src} items={state.items} start={state.index} onClose={() => setState(null)} />}
    </ViewerContext.Provider>
  )
}

/**
 * Full-screen viewer for a design: zoom (buttons, wheel, keys, double-click), drag to pan, rotate, flip, fit / 100%,
 * browser full screen, previous / next across a set, and download. Keys: + − 0 R F ← → Esc.
 */
function Viewer({ items, start, onClose }: { items: ViewerItem[]; start: number; onClose: () => void }) {
  const [index, setIndex] = useState(start)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [rotation, setRotation] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const item = items[index]!
  const many = items.length > 1

  const reset = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); setRotation(0); setFlipped(false) }, [])
  const go = useCallback((next: number) => { setIndex((next + items.length) % items.length); reset() }, [items.length, reset])
  const zoomBy = useCallback((factor: number) => setZoom((value) => { const next = Math.min(MAX, Math.max(MIN, value * factor)); if (next <= 1) setPan({ x: 0, y: 0 }); return next }), [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') zoomBy(1.25)
      else if (event.key === '-') zoomBy(0.8)
      else if (event.key === '0') reset()
      else if (event.key.toLowerCase() === 'r') setRotation((value) => value + (event.shiftKey ? -90 : 90))
      else if (event.key.toLowerCase() === 'f') setFlipped((value) => !value)
      else if (event.key === 'ArrowRight' && many) go(index + 1)
      else if (event.key === 'ArrowLeft' && many) go(index - 1)
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    const onFullscreen = () => setFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onFullscreen)
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('fullscreenchange', onFullscreen) }
  }, [go, index, many, reset, zoomBy])

  function toggleFullscreen() {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void root.current?.requestFullscreen?.()
  }

  function onPointerDown(event: ReactPointerEvent) {
    if (zoom <= 1) return
    drag.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  function onPointerMove(event: ReactPointerEvent) {
    const start = drag.current
    if (start) setPan({ x: start.panX + event.clientX - start.x, y: start.panY + event.clientY - start.y })
  }

  const button = 'flex size-9 items-center justify-center rounded-full text-white/80 transition-colors outline-none hover:bg-white/15 hover:text-white focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-30'

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm" />
        <DialogPrimitive.Content ref={root} aria-describedby={undefined} className="fixed inset-0 z-[60] flex flex-col text-white outline-none">
          <DialogPrimitive.Title className="sr-only">{item.title}</DialogPrimitive.Title>

          <header className="flex shrink-0 items-center gap-2 px-4 py-3">
            <p className="min-w-0 flex-1 truncate text-sm font-medium">
              {item.title}
              {many && <span className="ml-2 text-white/60 tabular-nums">{index + 1} / {items.length}</span>}
            </p>
            <div className="flex items-center gap-0.5 rounded-full bg-white/10 p-1" role="toolbar" aria-label="Image controls">
              <button type="button" className={button} onClick={() => zoomBy(0.8)} aria-label="Zoom out" disabled={zoom <= MIN}><ZoomOutIcon className="size-[18px]" /></button>
              <button type="button" onClick={reset} aria-label="Reset zoom" className="w-14 text-center text-xs font-medium tabular-nums text-white/80 hover:text-white">{Math.round(zoom * 100)}%</button>
              <button type="button" className={button} onClick={() => zoomBy(1.25)} aria-label="Zoom in" disabled={zoom >= MAX}><ZoomInIcon className="size-[18px]" /></button>
              <span aria-hidden className="mx-1 h-5 w-px bg-white/20" />
              <button type="button" className={button} onClick={reset} aria-label="Fit to screen" title="Fit"><ScanIcon className="size-[18px]" /></button>
              <button type="button" className={button} onClick={() => { setZoom(2); }} aria-label="Zoom to 200%" title="200%"><span className="text-[11px] font-semibold">2×</span></button>
              <span aria-hidden className="mx-1 h-5 w-px bg-white/20" />
              <button type="button" className={button} onClick={() => setRotation((value) => value - 90)} aria-label="Rotate left"><RotateCcwIcon className="size-[18px]" /></button>
              <button type="button" className={button} onClick={() => setRotation((value) => value + 90)} aria-label="Rotate right"><RotateCwIcon className="size-[18px]" /></button>
              <button type="button" className={button} onClick={() => setFlipped((value) => !value)} aria-label="Flip horizontally" aria-pressed={flipped}><FlipHorizontal2Icon className="size-[18px]" /></button>
              <span aria-hidden className="mx-1 h-5 w-px bg-white/20" />
              <button type="button" className={button} onClick={toggleFullscreen} aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}>{fullscreen ? <MinimizeIcon className="size-[18px]" /> : <MaximizeIcon className="size-[18px]" />}</button>
              <a className={button} href={item.src} download={item.fileName ?? `${item.title}.png`} aria-label="Download image"><DownloadIcon className="size-[18px]" /></a>
            </div>
            <DialogPrimitive.Close className={cn(button, 'ml-1')} aria-label="Close viewer"><XIcon className="size-5" /></DialogPrimitive.Close>
          </header>

          <div
            className={cn('relative min-h-0 flex-1 overflow-hidden select-none', zoom > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in')}
            onWheel={(event) => zoomBy(event.deltaY < 0 ? 1.12 : 0.89)}
            onDoubleClick={() => (zoom > 1 ? reset() : setZoom(2))}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={() => (drag.current = null)}
            onPointerCancel={() => (drag.current = null)}
          >
            <img
              src={item.src}
              alt={item.title}
              draggable={false}
              style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg) scaleX(${flipped ? -1 : 1})` }}
              className="absolute inset-0 m-auto max-h-full max-w-full object-contain transition-transform duration-150 ease-out"
            />
            {many && (
              <>
                <button type="button" onClick={() => go(index - 1)} aria-label="Previous image" className={cn(button, 'absolute top-1/2 left-4 size-11 -translate-y-1/2 bg-black/40')}><ChevronLeftIcon className="size-6" /></button>
                <button type="button" onClick={() => go(index + 1)} aria-label="Next image" className={cn(button, 'absolute top-1/2 right-4 size-11 -translate-y-1/2 bg-black/40')}><ChevronRightIcon className="size-6" /></button>
              </>
            )}
          </div>

          {many && (
            <ul className="no-scrollbar flex shrink-0 justify-center gap-2 overflow-x-auto px-4 py-3" aria-label="Images">
              {items.map((thumb, position) => (
                <li key={thumb.src}>
                  <button type="button" onClick={() => go(position)} aria-label={`Show ${thumb.title}`} aria-current={position === index} className={cn('block h-16 overflow-hidden rounded-lg border-2 outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-white/60', position === index ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100')}>
                    <img src={thumb.src} alt="" className="h-full w-auto object-cover" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
